import type { IJsonModel } from 'flexlayout-react';
import { panelKachelId } from './guide-kachel-id.js';

export type BuehnenModus = 'dokument' | 'tabs' | 'buehne';
export type BuehnenProfil = 'touch' | 'desktop';
export interface BasisTab { id: string; key: string; name: string }
export interface Anordnung { basis: BasisTab[]; layout: IJsonModel }
export interface GespeicherteAnordnung { version: number; am: string; anordnung: Anordnung; hinweis: string }
export interface BuehnenBezug { id: string; textblock: string; haus?: 'dev' | 'partner'; schnellbuehne?: { id: string; titel: string; html: string; nachBuehneId: string } }

type Knoten = Record<string, any>;
const kopie = <T,>(o: T): T => JSON.parse(JSON.stringify(o));
export function alleTabs(doc: IJsonModel): Knoten[] {
  const out: Knoten[] = [];
  const gehen = (n: Knoten) => { if (n.type === 'tab') out.push(n); else (n.children ?? []).forEach(gehen); };
  gehen(doc.layout); (doc.borders ?? []).forEach(gehen);
  return out;
}
function datei(value: unknown): string {
  if (typeof value !== 'string') return '';
  try { return decodeURIComponent(new URL(value, 'http://cui.local').pathname).split('/').pop() ?? ''; } catch { return ''; }
}
export function dokumentTab(doc: IJsonModel, textblock: string): Knoten | undefined {
  const tabs = alleTabs(doc);
  const name = datei(textblock);
  const url = (t: Knoten) => t.config?.url ?? t.config?.watchPath;
  const exact = name && tabs.find(t => datei(url(t)) === name);
  if (exact) return exact;
  // Native main page and answer view share seite.html. Identify the main page by
  // this stage's source filename and its exact relative route, never by tab order.
  const buehnenId = name.replace(/\.html?$/i, '');
  const native = buehnenId && tabs.filter(t => {
    const raw = url(t);
    if (typeof raw !== 'string' || !raw.startsWith('/') || raw.startsWith('//') || raw.includes('\\')) return false;
    try {
      const u = new URL(raw, 'http://cui.local');
      return u.origin === 'http://cui.local' && !u.search && !u.hash
        && u.pathname === `/api/guide/buehnen/${encodeURIComponent(buehnenId)}/seite.html`;
    } catch { return false; }
  });
  if (native && native.length === 1) return native[0];
  const html = tabs.filter(t => /\.html?$/i.test(datei(url(t))));
  return html.length === 1 ? html[0] : undefined;
}
export function basisTabs(doc: IJsonModel, textblock: string): BasisTab[] {
  const dokument = dokumentTab(doc, textblock);
  const counts = new Map<string, number>();
  return alleTabs(doc).map(t => {
    // Content identity survives recipe reordering; the main document may get a new URL.
    const raw = t === dokument ? 'dokument' : JSON.stringify([t.component,
      t.config?.initialSessionId ?? t.config?.url ?? t.config?.watchPath ?? t.config?.docId ?? t.name]);
    const count = counts.get(raw) ?? 0; counts.set(raw, count + 1);
    return { id: t.id, key: `${raw}:${count}`, name: t.name ?? t.component };
  });
}
export function bearbeitbar(doc: IJsonModel): IJsonModel {
  const next = kopie(doc);
  // Reiter brechen in eine zweite Zeile um, statt hinter einem kleinen Ueberlauf-Pfeil zu
  // verschwinden (03.10.2026, iPad: in schmalen Fenstergruppen waren Reiter nicht antippbar).
  next.global = { ...next.global, tabEnableClose: true, tabEnableDrag: true,
    tabSetEnableDrag: true, tabSetEnableDrop: true, tabSetEnableDivide: true,
    tabSetEnableTabStrip: true, tabSetEnableMaximize: true, tabSetEnableTabWrap: true, splitterSize: 8 };
  const gehen = (n: Knoten) => {
    if (n.type === 'tab') { n.enableClose = true; n.enableDrag = true; }
    if (n.type === 'tabset') { n.enableDrag = true; n.enableDrop = true; n.enableDivide = true; n.enableTabStrip = true; }
    (n.children ?? []).forEach(gehen);
  };
  gehen(next.layout); (next.borders ?? []).forEach(gehen);
  return next;
}
/**
 * „Nur das Dokument" und „Als Reiter" sind FESTE Ansichten (Rafael, 03.10.2026 10:19, iPad:
 * „Bitte nur das Dokument als Reiter einfach hintereinander … am Tablet … funktioniert das
 * nicht so wirklich"). Gemessen an seinem gespeicherten Touch-Stand: ein versehentlich
 * getipptes × hatte das Dokument aus den Reitern genommen, und der gemerkte zuletzt
 * gewaehlte Reiter schob es nach dem Neuladen aus dem Bild. Darum hier: alle Fenster in
 * EINER Reiterleiste, das Dokument zuerst und vorne, kein Schliessen, kein Ziehen, kein
 * Teilen — und nichts davon wird gespeichert. Wer frei anordnen will, nimmt
 * „Frei nebeneinander"; nur dort gilt die gespeicherte Anordnung.
 */
export function istFesteAnsicht(modus: BuehnenModus): boolean { return modus !== 'buehne'; }
function fest(doc: IJsonModel): IJsonModel {
  const next = kopie(doc);
  next.global = { ...next.global, tabEnableClose: false, tabEnableDrag: false,
    tabSetEnableDrag: false, tabSetEnableDrop: false, tabSetEnableDivide: false,
    tabSetEnableTabStrip: true, tabSetEnableMaximize: false, tabSetEnableTabWrap: true };
  const gehen = (n: Knoten) => {
    if (n.type === 'tab') { n.enableClose = false; n.enableDrag = false; }
    if (n.type === 'tabset') { n.enableDrag = false; n.enableDrop = false; n.enableDivide = false; n.enableTabStrip = true; n.enableMaximize = false; n.enableTabWrap = true; }
    (n.children ?? []).forEach(gehen);
  };
  gehen(next.layout);
  return next;
}
export function standardAnsicht(doc: IJsonModel, modus: BuehnenModus, textblock: string): IJsonModel {
  if (modus === 'buehne') return bearbeitbar(doc);
  const dokument = dokumentTab(doc, textblock);
  const tabs = alleTabs(doc);
  const geordnet = modus === 'dokument' ? (dokument ? [dokument] : [])
    : [...(dokument ? [dokument] : []), ...tabs.filter(t => t !== dokument)];
  return fest({ global: doc.global, borders: [], layout: { type: 'row', children: [
    { type: 'tabset', selected: 0, active: true, children: kopie(geordnet) as any },
  ] } });
}
export interface DateiAnordnungsKontext { haus?: 'dev' | 'partner'; origin?: string }
/** Only known authenticated document routes identify a file. A foreign lookalike URL, a
 * name, or an arbitrary app URL must never alias a saved panel. This grants no file access. */
export function dateiIdentitaet(tab: Knoten, kontext: DateiAnordnungsKontext, alteAuslieferung = false): { haus: string; path: string } | null {
  const c = tab.config ?? {};
  let path: unknown, haus: unknown;
  if (tab.component === 'preview') {
    path = c.datei ?? c.watchPath; haus = c.haus ?? kontext.haus;
  } else if (tab.component === 'browser' && typeof c.url === 'string') {
    try {
      const relative = c.url.startsWith('/') && !c.url.startsWith('//');
      if (!relative && !kontext.origin) return null;
      const erwartetesOrigin = kontext.origin ?? 'http://cui.invalid';
      const u = new URL(c.url, erwartetesOrigin);
      if (u.origin !== erwartetesOrigin) return null;
      if (u.username || u.password || u.hash) return null;
      // Legacy own-house static single-file route, only when reading a saved predecessor.
      // New panels must use the protected document routes above; no arbitrary host/path proxy.
      if (alteAuslieferung && /^\/downloads\/entscheidungen\/[^/]+$/.test(u.pathname) && !u.search) {
        const name = decodeURIComponent(u.pathname.slice('/downloads/entscheidungen/'.length));
        if (!name || name.includes('/') || name.includes('\\') || name.includes('\0') || ['.', '..'].includes(name) || !kontext.haus) return null;
        return { haus: kontext.haus, path: '/root/projekte/koordination/entscheidungen/' + name };
      }
      if (!['/api/file', '/api/file/download', '/api/file/render-md'].includes(u.pathname)) return null;
      if ([...u.searchParams.keys()].some(k => !['path', 'haus', 'inline'].includes(k))) return null;
      if (u.searchParams.getAll('path').length !== 1 || u.searchParams.getAll('haus').length !== 1 || u.searchParams.getAll('inline').length > 1) return null;
      if (u.pathname === '/api/file/download' && !['1', 'true'].includes(u.searchParams.get('inline') ?? '')) return null;
      path = u.searchParams.get('path'); haus = u.searchParams.get('haus');
    } catch { return null; }
  }
  if (!['dev', 'partner'].includes(String(haus)) || typeof path !== 'string' || !path.startsWith('/') || path.includes('\\') || path.includes('\0') || path.split('/').some(p => p === '.' || p === '..')) return null;
  return { haus: String(haus), path };
}
/** Explicit artifact succession belongs to the advisor's recipe, not the human's layout.
 * It only preserves placement; the evidence text never grants access to either file. */
function dateiVorgaenger(tab: Knoten, aktuell: { haus: string; path: string }): Array<{ haus: string; path: string }> {
  const refs = tab.config?.dateiVorgaenger;
  if (tab.component !== 'browser' || !Array.isArray(refs) || refs.length > 10) return [];
  return refs.flatMap(ref => {
    if (!ref || ref.haus !== aktuell.haus || typeof ref.pfad !== 'string' || ref.pfad.length > 2000
      || typeof ref.beleg !== 'string' || !ref.beleg.trim() || ref.beleg.length > 2000) return [];
    const ident = dateiIdentitaet({ component: 'preview', config: { datei: ref.pfad, haus: ref.haus } }, {});
    return ident ? [ident] : [];
  });
}
function dateiAliase(tabs: Knoten[], basis: BasisTab[], gespeichert: Anordnung, kontext: DateiAnordnungsKontext): Map<string, string> {
  const aktuelle = tabs.map((tab, i) => ({ tab, basis: basis[i], datei: dateiIdentitaet(tab, kontext) }));
  const alteTabs = new Map(alleTabs(gespeichert.layout).map(t => [t.id, t]));
  const aliases = new Map<string, string>();
  for (const alt of gespeichert.basis) {
    // Exact current keys already have their normal semantics, including duplicates.
    if (basis.some(b => b.key === alt.key)) continue;
    let tab = alteTabs.get(alt.id);
    if (!tab) {
      // Closed browser tabs retain their complete URL in the old content key.
      const raw = /^(.*):\d+$/.exec(alt.key);
      try {
        const pair = raw && JSON.parse(raw[1]);
        if (Array.isArray(pair) && pair.length === 2 && pair[0] === 'browser' && typeof pair[1] === 'string')
          tab = { component: 'browser', config: { url: pair[1] } };
      } catch { /* A legacy name key is not file evidence. */ }
    }
    const ident = tab && dateiIdentitaet(tab, kontext, true);
    const candidates = aktuelle.filter(a => {
      if (!a.datei) return false;
      const vorgaenger = [a.datei, ...dateiVorgaenger(a.tab, a.datei)];
      if (ident) return vorgaenger.some(v => ident.haus === v.haus && ident.path === v.path);
      // A closed legacy preview has no config in its saved layout. Its content-derived ID
      // can verify the original single-file recipe. Never guess from its old display name.
      const match = /^#?guide-p-preview-(\d+)-[a-z0-9]+$/.exec(alt.id);
      if (!match || kontext.haus !== a.datei.haus) return false;
      return vorgaenger.some(v => ['datei', 'watchPath'].some(field => [false, true].some(withHouse => {
        const config = { [field]: v.path, ...(withHouse ? { haus: v.haus } : {}) };
        return panelKachelId('preview', Number(match[1]), config).replace(/^#/, '') === alt.id.replace(/^#/, '');
      })));
    });
    if (candidates.length === 1) aliases.set(alt.key, candidates[0].basis.key);
  }
  // Ambiguous aliases must not merge two independently arranged panels.
  const counts = new Map<string, number>();
  for (const key of aliases.values()) counts.set(key, (counts.get(key) ?? 0) + 1);
  for (const [old, key] of aliases) if (counts.get(key)! > 1 || gespeichert.basis.some(b => b.key === key)) aliases.delete(old);
  return aliases;
}

/** Reuse arrangement, never stale panel URLs/configs. Removed recipe panels stay gone;
 * newly introduced panels are appended; panels the user closed stay closed. */
export function wiederherstellen(doc: IJsonModel, textblock: string, gespeichert?: Anordnung, kontext: DateiAnordnungsKontext = {}): IJsonModel {
  if (!gespeichert) return bearbeitbar(doc);
  const basis = basisTabs(doc, textblock);
  const tabs = alleTabs(doc);
  const aktuell = new Map(basis.map((b, i) => [b.key, tabs[i]]));
  const aliase = dateiAliase(tabs, basis, gespeichert, kontext);
  const vorher = new Map(gespeichert.basis.map(b => [b.id, aliase.get(b.key) ?? b.key]));
  const bekannt = new Set(vorher.values());
  const benutzt = new Set<string>();
  const bauen = (n: Knoten): Knoten | null => {
    if (n.type === 'tab') {
      const key = vorher.get(n.id); const tab = key && aktuell.get(key);
      if (!tab || benutzt.has(key!)) return null;
      benutzt.add(key!); return kopie(tab);
    }
    if (!['row', 'tabset', 'border'].includes(n.type)) return null;
    const selectedId = n.children?.[n.selected ?? 0]?.id;
    const children = (n.children ?? []).map(bauen).filter(Boolean);
    if (!children.length) return null;
    const newSelected = children.findIndex((t: Knoten) => vorher.get(selectedId) === basis.find(b => b.id === t.id)?.key);
    return { type: n.type, ...(n.id ? { id: n.id } : {}), ...(Number.isFinite(n.weight) ? { weight: n.weight } : {}),
      ...(n.type === 'border' ? { location: n.location } : {}),
      ...(n.type === 'tabset' ? { selected: Math.max(0, newSelected), ...(n.active ? { active: true } : {}) } : {}), children };
  };
  const layout = bauen(gespeichert.layout.layout) ?? { type: 'row', children: [] };
  /*
   * Mindestbreite: Eine gespeicherte Gruppe unter 5 % ihrer Zeile ist unbedienbar
   * (21.10.2026 bei Rafael: 0,09 % — darin lag die Entscheidungsgrundlage mit der
   * Beratung, unsichtbar). Solche Gruppen bekommen beim Wiederherstellen ein Viertel
   * der Zeile; Reihenfolge, Reiter und Aufteilung bleiben wie gespeichert.
   */
  const mindestbreite = (n: Knoten) => {
    const kinder: Knoten[] = n.children ?? [];
    const gewicht = (k: Knoten) => (Number.isFinite(k.weight) ? k.weight : 100);
    const summe = kinder.reduce((s, k) => s + gewicht(k), 0);
    if (kinder.length > 1 && summe > 0) for (const k of kinder) if (gewicht(k) / summe < 0.05) k.weight = summe * 0.25;
    kinder.forEach(mindestbreite);
  };
  mindestbreite(layout);
  const neue = basis.filter(b => !bekannt.has(b.key)).map(b => kopie(aktuell.get(b.key)!));
  const borders = (gespeichert.layout.borders ?? []).map(bauen).filter(Boolean);
  /*
   * Neue Fenster (z. B. eine nachtraeglich angehaengte Deploy-Kachel) kommen in die
   * GROESSTE Gruppe und werden dort ausgewaehlt. Vorher landeten sie in der ersten
   * Gruppe, egal wie gross: am 21.10.2026 war die bei Rafael auf 0,09 % Breite
   * zusammengeschoben, die vorbereitete Deploy-App existierte, war aber unsichtbar.
   * Ein neues Fenster ist genau das, was der Berater zeigen will — also vorne.
   */
  let groessteGruppe: Knoten | undefined; let groessterAnteil = -1;
  const finde = (n: Knoten, anteil: number) => {
    const kinder = n.children ?? [];
    const summe = kinder.reduce((s: number, k: Knoten) => s + (Number.isFinite(k.weight) ? k.weight : 100), 0) || 1;
    if (n.type === 'tabset' && anteil > groessterAnteil) { groessteGruppe = n; groessterAnteil = anteil; }
    kinder.forEach((k: Knoten) => finde(k, anteil * ((Number.isFinite(k.weight) ? k.weight : 100) / summe)));
  };
  finde(layout, 1);
  if (groessteGruppe && neue.length) {
    groessteGruppe.children.push(...neue);
    // During a file migration the user-selected surviving tab keeps focus, even if the
    // newer recipe also adds documents. Ordinary new-panel behavior is otherwise unchanged.
    if (!aliase.size) groessteGruppe.selected = groessteGruppe.children.length - neue.length;
  } else if (!groessteGruppe) layout.children.push({ type: 'tabset', selected: 0, children: neue });
  return bearbeitbar({ global: doc.global, borders: borders as any, layout: layout as any });
}
export function anordnungsHinweis(a: Anordnung): string {
  const sichtbar = alleTabs(a.layout);
  const ids = new Set(sichtbar.map(t => t.id));
  const geschlossen = a.basis.filter(t => !ids.has(t.id)).map(t => t.name);
  const gruppen: string[] = [];
  const gehen = (n: Knoten) => { if (n.type === 'tabset') gruppen.push((n.children ?? []).map((t: Knoten) => t.name).join(' → ')); else (n.children ?? []).forEach(gehen); };
  gehen(a.layout.layout);
  return `Fenstergruppen / Tab-Reihenfolge: ${gruppen.join(' | ') || 'alle geschlossen'}. Geschlossen: ${geschlossen.join(', ') || 'keine'}. Größen und Aufteilung stehen im gespeicherten Layout.`;
}
